// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { Permission } from '../constants/permissions.js';
/**
 * Collection names, declared beside the types they hold.
 *
 * The 62.7.0 convention. A collection name spelled a second time in
 * `indexes.ts`, a migration and a service is three chances to index one
 * collection and query another — which fails as a slow query rather than as an
 * error, and so is never noticed.
 */
export const MANUFACTURING_PRODUCT_COLLECTION = 'grc_manufacturing_products';
export const MANUFACTURING_BATCH_COLLECTION = 'grc_manufacturing_batches';
export const MANUFACTURING_TRACE_EVENT_COLLECTION = 'grc_manufacturing_trace_events';
export const MANUFACTURING_CONFORMITY_COLLECTION = 'grc_manufacturing_conformity_records';
export const MANUFACTURING_CAPA_COLLECTION = 'grc_manufacturing_capa_records';
export const MANUFACTURING_EPR_SCOPE_COLLECTION = 'grc_manufacturing_epr_scopes';
export const MANUFACTURING_EPR_VOLUME_COLLECTION = 'grc_manufacturing_epr_volumes';
export const MANUFACTURING_EPR_FULFILMENT_COLLECTION = 'grc_manufacturing_epr_fulfilments';
export const MANUFACTURING_EPR_SUBMISSION_COLLECTION = 'grc_manufacturing_epr_submissions';
export const MANUFACTURING_SUBSTANCE_COLLECTION = 'grc_manufacturing_substances';
export const MANUFACTURING_COMPOSITION_COLLECTION = 'grc_manufacturing_compositions';
export const MANUFACTURING_OT_PROFILE_COLLECTION = 'grc_manufacturing_ot_profiles';
export const MANUFACTURING_REMOTE_ACCESS_COLLECTION = 'grc_manufacturing_remote_access_grants';
/** Sprint 62.49.0 (AC19, D15) — product and batch field values per write, for the as-of read. */
export const MANUFACTURING_MASTER_DATA_VERSION_COLLECTION = 'grc_manufacturing_master_data_versions';
/**
 * How a traced unit's identifier is physically carried.
 *
 * **Why this is a platform union and not a pack vocabulary.** These are global
 * technical standards for machine-readable marks. No state enacted DataMatrix and
 * no authority supervises NFC; a jurisdiction that requires a particular one
 * requires it *by reference*. R-46's subject is a framework code, an instrument
 * number, an authority name or a jurisdiction-specific tier vocabulary, and a
 * barcode symbology is none of those.
 *
 * What **is** the jurisdiction's to prescribe is the identifier *scheme* — what
 * the number means and who issues it — and that is the pack's
 * `trace_identifier_scheme` vocabulary. The distinction matters: two products may
 * carry the same scheme on different carriers, and the same carrier under
 * different schemes.
 */
export const TRACE_IDENTIFIER_CARRIERS = [
    'qr',
    'datamatrix',
    'rfid',
    'nfc',
    'human_readable',
];
/**
 * Sprint 62.47.0 BC-1 (owner answer Q2, "refuse, never rewrite") — the three
 * changes the global request sanitiser (`middleware/sanitize.ts`) makes to a
 * string, in the order it makes them, named so a refused identifier value can
 * say which one it would have suffered. A platform property of the request
 * filter, not of any identifier scheme.
 */
export const SANITIZER_CHANGES = ['markup', 'non_nfc', 'surrounding_whitespace'];
/**
 * Sprint 62.47.0 BC-1 — the refusals on a path that stores a trace identifier
 * value (`identifiers[].value` on a product or batch, and on a batch
 * correction proposal). Both are 400s.
 *
 * - `VALUE_WOULD_CHANGE`: the value as received would be changed by the
 *   sanitiser. `details.field_errors` names each field path with a sentence and
 *   `details.identifier_refusals` lists `{ field, changes }`.
 * - `RAW_JSON_REQUIRED`: the request carries identifiers but no JSON body to
 *   read them from as received (for example a url-encoded form), so nothing can
 *   be stored as entered.
 */
export const IDENTIFIER_VALUE_REFUSAL_CODES = {
    VALUE_WOULD_CHANGE: 'identifier_value_would_change',
    RAW_JSON_REQUIRED: 'identifier_raw_json_required',
};
/**
 * Sprint 62.47.0, third `legal` reading P3-C4 — the longest identifier value a
 * field holds. A generic storage limit, the same for every scheme: a longer value
 * is refused, never shortened, and the refusal never says the value is invalid
 * (nothing checks it against any scheme). Read by the request schema and by the
 * product and batch forms, which refuse the save before sending.
 */
export const TRACE_IDENTIFIER_VALUE_MAX_LENGTH = 200;
// ─────────────────────────────────────────────────────────────────
// The product master
// ─────────────────────────────────────────────────────────────────
/**
 * The single product record every other domain in this sprint points at.
 *
 * AC1: *"One product links role/category/HS, identifiers, batches/lots/serials,
 * suppliers and all domain evidence without duplication."* The "without
 * duplication" half is a property of every **other** type in this file — none of
 * them carries a product name, a category or an HS code — and it is asserted by a
 * test that enumerates the domain record types and fails on any that declares a
 * field this one already owns. A rule about duplication that only inspected this
 * type would be scoped narrower than its subject.
 *
 * `commercial_role` is the organisation's own position with respect to the goods.
 * It is a platform concept: whether you made a thing or imported it changes which
 * duties attach in every jurisdiction that has such duties, and nowhere is the
 * distinction itself the invention of one instrument.
 */
export const PRODUCT_COMMERCIAL_ROLES = [
    'manufacturer',
    'importer',
    'distributor',
    'own_brand_reseller',
    'contract_manufacturer',
];
export const PRODUCT_LIFECYCLE_STATES = [
    'draft',
    'in_development',
    'on_market',
    'withdrawn',
    'discontinued',
];
/**
 * A batch, lot or serialised unit of a product.
 *
 * One type for all three, discriminated by `granularity`, because the trace
 * ledger, the recall propagation and the EPR volume roll-up all treat them
 * identically — they are "the thing an event happened to". Three types would have
 * meant three near-identical readers, and 62.7.0's registry is in the tree
 * precisely because that shape drifts.
 *
 * `product_id` is non-nullable: a batch of nothing is not a record this product
 * has a use for.
 */
export const BATCH_GRANULARITIES = ['batch', 'lot', 'serial'];
/** Sprint 62.49.0 (AC8) — whether a batch records any input lot. Platform vocabulary. */
export const BATCH_FOOD_INPUT_LINK_STATES = ['recorded', 'missing'];
/**
 * Sprint 62.49.0 (Slice D, AC19) — `grc_manufacturing_master_data_versions`:
 * one row per product or batch write, holding the values of the fields that
 * write changed, before and after. Written beside the `$set` by the product
 * service, so an as-of read returns the value a field held on a date — the
 * audit row holds field NAMES only (work file E0 row 18), so it cannot.
 * Append-only: no row is ever updated.
 */
export const MASTER_DATA_VERSION_TARGETS = ['product', 'batch'];
// ─────────────────────────────────────────────────────────────────
// Conformity
// ─────────────────────────────────────────────────────────────────
/**
 * Where a conformity assessment has got to.
 *
 * `nonconforming` is a state the organisation records, not a verdict the platform
 * reaches. Nothing in the platform transitions a record into it: a person does,
 * and the regime's determination caveat is rendered beside it.
 */
export const CONFORMITY_STATES = [
    'not_started',
    'in_assessment',
    'evidence_pending',
    'declared_conforming',
    'nonconforming',
    'superseded',
];
// ─────────────────────────────────────────────────────────────────
// Corrective action and recall
// ─────────────────────────────────────────────────────────────────
export const CAPA_STATES = [
    'open',
    'investigating',
    'action_in_progress',
    'awaiting_closure_approval',
    'closed',
    'cancelled',
];
/**
 * How far a recall has reached.
 *
 * `market_withdrawal` and `consumer_recall` are kept distinct because the duties
 * differ everywhere such duties exist: pulling stock from a distributor is not the
 * same act as asking the public to return a product, and a single "recall" state
 * would make a record unable to say which one the organisation did.
 */
export const RECALL_SCOPES = ['internal_only', 'market_withdrawal', 'consumer_recall'];
/**
 * The four conditions an EPR reconciliation can detect.
 *
 * A **zero-import leaf**, read by the service, by `routes.ts` and by
 * `scripts/validate-pack-content.ts` alike — 62.7.0's arrangement, for its
 * reason: importing the service to reach a string list would drag Express, the
 * Mongo driver and the audit hook into a content lint, and the alternative, a
 * second copy inside the script, is 62.0.11's defect exactly.
 *
 * Each is decided by a presence check or a numeric comparison, and **none of them
 * is a word any instrument uses**. What the reconciliation detects is the
 * platform's; what it is called is the pack's, joined through a term's
 * `covers_condition`.
 */
export const EPR_RECONCILIATION_CONDITIONS = [
    /** Volumes are recorded for a period, and no submission covers it. */
    'volume_without_submission',
    /** A submission exists for a period with no volumes recorded against it. */
    'submission_without_volume',
    /** The sum of recorded volumes differs from the quantity the submission declared. */
    'declared_quantity_mismatch',
    /** A fulfilment record's unit differs from the volume records it answers. */
    'unit_mismatch',
];
// ─────────────────────────────────────────────────────────────────
// Industrial OT
// ─────────────────────────────────────────────────────────────────
/**
 * What an OT system is integrated with.
 *
 * **Product categories, not instruments.** A manufacturing execution system is a
 * class of software; no state enacted it and no authority supervises the term. As
 * with `TRACE_IDENTIFIER_CARRIERS`, R-46's subject is a framework code, an
 * instrument number, an authority name or a jurisdiction-specific tier
 * vocabulary, and none of these is any of those.
 */
export const OT_INTEGRATION_KINDS = [
    'mes',
    'erp',
    'scada_historian',
    'cloud_analytics',
    'vendor_remote_support',
    'other',
];
/**
 * A grant with the state a surface renders, derived at read time from the clock.
 *
 * ## Four states, and the precedence between them
 *
 * `pending_approval` was missing, and its absence is what made an unapproved grant
 * read `active` — see `approved_by`. With it, the precedence is:
 *
 * 1. **`revoked`** — somebody decided. It beats everything, because a decision is a
 *    fact about a person and the other three are facts about a clock or a gap.
 * 2. **`expired`** — the window has closed, so no access is possible regardless of
 *    approval. An unapproved grant that expired therefore reads `expired`, not
 *    `pending_approval`: the reader's question is "can this vendor get in", and the
 *    answer is no for the stronger reason. That somebody never approved it stays
 *    readable on the record, where `approved_by` is null.
 * 3. **`pending_approval`** — requested, in window, and nobody has approved it.
 *    **No access.**
 * 4. **`active`** — approved, in window, not revoked. The only state in which a
 *    vendor can reach the machinery.
 *
 * Derived, never stored: a stored flag is wrong the moment the clock passes
 * `expires_at` and nothing runs to correct it.
 */
export const REMOTE_ACCESS_GRANT_STATES = [
    'pending_approval',
    'active',
    'expired',
    'revoked',
];
// ─────────────────────────────────────────────────────────────────
// Exports (R-33)
// ─────────────────────────────────────────────────────────────────
/**
 * The registers this module can export, declared **once** and read by both sides.
 *
 * It lived in `manufacturing-export-service.ts` and was therefore unreachable from the
 * frontend, so the api-client would have had to restate it — and a restated union is
 * how a ninth kind comes to have a route and no client. That is not hypothetical here:
 * Gate E12.5 pass 2 (P1-2) found all eight of these routes shipped, correct and
 * tested, with `ExportFormatMenu` on **0 of the module's nine pages** and no client
 * method at all. The route table on the backend and the export control on the frontend
 * now derive from the same array.
 *
 * Each kind is gated on its own sub-domain read permission, never one blanket export
 * grant — 64.0.0's rule that retrieval honours the permission that gated creation.
 */
export const MANUFACTURING_EXPORT_KINDS = [
    'conformity',
    'trace_events',
    'corrective_actions',
    'epr_scopes',
    'epr_submissions',
    'substances',
    'compositions',
    'remote_access_grants',
];
/**
 * Sprint 64.92.0 — which grant gates each register's export, declared **here**
 * so creation and retrieval read one table.
 *
 * ## Why this moved out of the backend service
 *
 * It was `KIND_SPECS[kind].permission` in `manufacturing-export-service.ts`, and
 * the route table read it correctly: eight export routes, each requiring its own
 * register's read permission. Retrieval read something else — a single
 * `manufacturing_export` owner type mapped in `OWNER_TYPE_DOWNLOAD_POLICY` to
 * `manufacturing_product:read`. So creation was gated per register and retrieval
 * was gated per module, and **seven of the eight** were therefore retrievable by
 * a broader population than could produce them: a holder of
 * `manufacturing_product:read` alone could download an exported substance
 * register or a remote-access grant register.
 *
 * That is 64.0.0's rule — *retrieval honours the permission that gated creation*
 * — failing in the broad direction, and its narrow mirror (idea-catalog #140) had
 * the same single cause: **one owner type per module**. The remedy is not more
 * owner types. It is to resolve the retrieval grant from the export's own kind,
 * which is already recorded on the artefact at creation
 * (`metadata.kind`), and which this table turns into a permission.
 *
 * `Record<ManufacturingExportKind, Permission>` is total, so a ninth register
 * cannot arrive with a route and no retrieval grant — that is a compile error
 * here, not a silent inheritance of a sibling register's permission.
 *
 * Six distinct grants over eight registers (measured, not asserted from the
 * "five sub-domains" the service's own comment claims — `epr_scopes` and
 * `epr_submissions` share one, `substances` and `compositions` share one, and
 * the other four stand alone). The service reads its `KIND_SPECS.permission`
 * from this table rather than restating it.
 */
export const MANUFACTURING_EXPORT_GATES = {
    conformity: Permission.MANUFACTURING_CONFORMITY_READ,
    trace_events: Permission.MANUFACTURING_PRODUCT_READ,
    corrective_actions: Permission.MANUFACTURING_RECALL_READ,
    epr_scopes: Permission.MANUFACTURING_EPR_READ,
    epr_submissions: Permission.MANUFACTURING_EPR_READ,
    substances: Permission.MANUFACTURING_SUBSTANCE_READ,
    compositions: Permission.MANUFACTURING_SUBSTANCE_READ,
    remote_access_grants: Permission.MANUFACTURING_OT_READ,
};
/** The four formats `UnifiedExportService` produces. Presentation order. */
export const MANUFACTURING_EXPORT_FORMATS = ['csv', 'xlsx', 'pdf', 'docx'];
//# sourceMappingURL=manufacturing.js.map